/*
 * Behavior: mouse use looks synthetic. Two signals:
 *   1. A fabricated (script-dispatched) mouse / pointer / touch / click event is
 *      not trusted => bot.
 *   2. The movement that happened is robotic (constant speed, straight lines, one
 *      narrow axis) — judged by lib/bots/mouseBehavior after a watch window.
 *
 * Only real mouse movement (pointermove with pointerType 'mouse', no button held)
 * feeds the movement analysis, so touch and drags never pollute it. Reports via
 * the shared callback from useBotDetection.
 */
import { useEffect, useRef } from 'react'
import { analyzeMouseBehavior, MousePoint } from 'lib/bots/mouseBehavior'
import { TrackBotTrafficAction } from 'lib/bots/trackBotTraffic'

// How long (in milliseconds) to watch the mouse before judging the path quality.
const WATCH_DURATION_MS = 12000
// Keep at most one sample per this many milliseconds, to limit how many we store.
const SAMPLE_EVERY_MS = 50
// Most points to keep (a count); the oldest is dropped once we pass this.
const MAX_MOUSE_POINTS = 1000

const useMouseBehaviorBotCheck = (report: (action: TrackBotTrafficAction) => void): void => {
  const mousePoints = useRef<MousePoint[]>([])
  const lastSampleTimeMs = useRef(0)

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    // Only genuine mouse movement counts. Touch / pen movement is ignored here so
    // the browser's synthetic touch-to-mouse events cannot pollute the analysis.
    const handlePointerMove = (event: PointerEvent) => {
      if (event.pointerType !== 'mouse') {
        return
      }
      // A fabricated (dispatched) mouse move is never trusted.
      if (!event.isTrusted) {
        report('mouse')
        return
      }

      // Skip movement while a button is held: dragging a slider, scrollbar, or
      // doing drag-and-drop is legitimately axis-constrained and would otherwise
      // trip the straight-line / narrow-axis checks. Only free movement is judged.
      if (event.buttons !== 0) {
        return
      }

      if (event.timeStamp - lastSampleTimeMs.current < SAMPLE_EVERY_MS) {
        return
      }
      lastSampleTimeMs.current = event.timeStamp

      const points = mousePoints.current
      points.push({ x: event.clientX, y: event.clientY, t: event.timeStamp })
      if (points.length > MAX_MOUSE_POINTS) {
        points.shift()
      }
    }

    // A fabricated pointer / touch / click event is never trusted.
    const handlePointerDown = (event: PointerEvent) => {
      if (!event.isTrusted) {
        report('mouse')
      }
    }

    const handleTouchStart = (event: TouchEvent) => {
      if (!event.isTrusted) {
        report('mouse')
      }
    }

    const handleClick = (event: MouseEvent) => {
      if (!event.isTrusted) {
        report('mouse')
      }
    }

    window.addEventListener('pointermove', handlePointerMove, { passive: true })
    window.addEventListener('pointerdown', handlePointerDown, { passive: true, capture: true })
    window.addEventListener('touchstart', handleTouchStart, { passive: true, capture: true })
    window.addEventListener('click', handleClick, { passive: true, capture: true })

    const watchTimer = window.setTimeout(() => {
      // Judge the quality of whatever real mouse movement happened (constant
      // speed, straight lines, one narrow axis).
      if (analyzeMouseBehavior(mousePoints.current)) {
        report('mouse')
      }
    }, WATCH_DURATION_MS)

    return () => {
      window.removeEventListener('pointermove', handlePointerMove)
      window.removeEventListener('pointerdown', handlePointerDown, { capture: true })
      window.removeEventListener('touchstart', handleTouchStart, { capture: true })
      window.removeEventListener('click', handleClick, { capture: true })
      window.clearTimeout(watchTimer)
    }
  }, [])
}

export default useMouseBehaviorBotCheck
